import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { Body } from '#/components/Text/Body';
import { BodySmall } from '#/components/Text/BodySmall';
import { RichTextHeadline } from '#/components/Text/RichTextHeadline';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiImageBlockData } from '#/modules/cms/api/types/blocks/CmsApiImageBlock';
import { colors } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';

import { CmsImageBlockImageOrFigureHolder } from './CmsImageBlock.styled';
import { CmsImageBlockImage } from './CmsImageBlockImage';

type Props = {
  data: CmsApiImageBlockData;
  className?: string;
  fullWidthLayout?: boolean;
  blockIndex: number;
  pageUpdated: string;
};

export function CmsImageBlock({
  data: { headline, subtitle, image, url, width, keepDimensions },
  className,
  fullWidthLayout,
  blockIndex,
  pageUpdated,
}: Props) {
  const { i18n } = useI18n();

  const fullWidthImage = width === 'FULL_WIDTH';

  const imageNode = (
    <CmsImageBlockImage
      image={image}
      keepDimensions={keepDimensions}
      fullWidthImage={fullWidthImage}
      fullWidthLayout={Boolean(fullWidthLayout)}
    />
  );

  return (
    <figure
      className={className}
      style={{ margin: 0, width: '100%' }}
      data-qa-id="cms-image-block"
      data-qa-full-width={fullWidthImage}
      data-qa-keep-dimensions={Boolean(keepDimensions)}
    >
      {(headline ?? subtitle) && (
        <figcaption style={{ marginBottom: 48 }}>
          {headline && (
            <RichTextHeadline textAlignCenter={fullWidthLayout}>
              {headline}
            </RichTextHeadline>
          )}
          {subtitle && (
            <Body
              analyticsData={{
                eventName: 'CMS Block',
                eventProperties: {
                  block_position: blockIndex,
                  block_type: 'Image',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
              style={{ textAlign: fullWidthLayout ? 'center' : 'left' }}
            />
          )}
        </figcaption>
      )}

      <CmsImageBlockImageOrFigureHolder
        as={headline || subtitle ? 'figure' : 'div'}
      >
        {url ? (
          <StyledNativeLinkWithRef
            href={url}
            onClick={(event) =>
              trackAnchorClicked(event, 'CMS Block', {
                block_position: blockIndex,
                block_type: 'Image',
                cta_type: 'Image',
                page_updated: pageUpdated,
              })
            }
            aria-label={image.altText ?? i18n('Follow image link')}
          >
            {imageNode}
          </StyledNativeLinkWithRef>
        ) : (
          imageNode
        )}

        {image.caption && (
          <div style={{ marginTop: 8 }}>
            <BodySmall
              tag="figcaption"
              style={{ color: colors.lightContentGrey }}
            >
              {image.caption}
            </BodySmall>
          </div>
        )}
      </CmsImageBlockImageOrFigureHolder>
    </figure>
  );
}
